import moment from "moment";
import {
  AddUserInput,
  AffiliateStatusHistoryProps,
  UpdateBatchProps,
  UpdateUserMutationInput,
  updateAffiliateStatusProps,
  updateConsumerPayload,
  UserEntityInput,
} from "../../interfaces/types";
import { mutation, query } from "../../utils/apollo";
import { getSelectedAffiliateId } from "../../utils/common-utils";
import {
  QueryAccountsArgs,
  QueryAffiliateFilesArgs,
  QueryTransactionsArgs,
  QueryAffiliateAccountDetailArgs
} from "../../../graphql";

export const fetchUserRole = () => {
  return query(`
        query userCurrentRole {
            userCurrentRole{
                code
                title
                roleLevel
                entityCode
                entityName
            }
        }
    `);
};

export const fetchMetaData = () => {
  return query(`
    query metadata {
      metadata
    }
  `);
};

export const fetchPartners = () => {
  return query(`
    query partners {
      partners {  
          addresses {
              address1
              address2
              addressType
              city
              country
              postalCode
              state
          }
          businessEmail
          dbaName
          establishedDate
          id
          legalName
          originCountry
          phoneDetails {
              countryDialingCode
              phoneNumber
              type
          }
          websiteAddress
      }
  }`);
};

export const fetchAffiliates = (affiliateProps: any) => {
  const { pageSize, pageNumber, status, sortBy } = affiliateProps;
  return query(
    `query affiliates ($limit: Int, $offset: Int, $partnerId: ID, $status: String, $sortBy: String) {
    affiliates (limit: $limit, offset: $offset, partnerId: $partnerId, status: $status, sortBy: $sortBy) {
        nodes {
            accountId
            createdOn
            additionalData
            businessProfile {
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                beneficialOwners {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                }
                businessEmail
                controlPersons {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                }
                dbaName
                establishedDate
                fundingAccount
                kybEvaluations {
                  id
                  entityId
                  flowCode
                  hostCode
                  evaluationStatus
                  evaluationDateTime
                  description
                  lastStatusChangedOn
                  lastStatusChangeComments
                  pendingDocuments {
                    type
                    isMandatory
                  }
                  hostEvaluationStatus
                  hostEvaluationResponse
                }
                kybStatus
                legalName
                originCountry
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
                taxIdNumber
                websiteAddress
                businessNature
            }
            id
          
            partnerId
            status
            statusName
            type
        }
        pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            limit
            offset
            startCursor
            totalPages
            totalRecords
        }
    }
}`,
    {
      limit: pageSize,
      offset: pageNumber,
      status: status,
      sortBy,
    },
  );
};

export const fetchAffiliatesForSelectOptions = (affiliateProps: any) => {
  const { pageSize, pageNumber, status, sortBy } = affiliateProps;
  return query(
    `query affiliates ($limit: Int, $offset: Int, $partnerId: ID, $status: String, $sortBy: String) {
      affiliates (limit: $limit, offset: $offset, partnerId: $partnerId, status: $status, sortBy: $sortBy) {
        nodes {
          accountId
          businessProfile {
            dbaName
            legalName
          }
          id
          partnerId
          status
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          limit
          offset
          startCursor
          totalPages
          totalRecords
        }
      }
    }`,
    {
      limit: pageSize,
      offset: pageNumber,
      status: status,
      sortBy,
    },
  );
};

export const fetchAffiliatesByPartnerId = (affiliateProps: any) => {
  const {
    pageSize,
    pageNumber,
    partnerId,
    status,
    legalName,
    createdOnStartDate,
    createdOnEndDate,
    approvedOnStartDate,
    approvedOnEndDate,
    salesRepresentative,
    sortBy,
  } = affiliateProps;
  return query(
    `query affiliates ($limit: Int, $offset: Int, $partnerId: ID, $status: String, 
      $legalName: String, $createdOnStartDate: String, $createdOnEndDate: String, 
      $approvedOnStartDate: String, $approvedOnEndDate: String, $salesRepresentative: String, $sortBy: String) {
    affiliates (limit: $limit, offset: $offset, partnerId: $partnerId, status: $status,
    legalName: $legalName, createdOnStartDate: $createdOnStartDate, createdOnEndDate: $createdOnEndDate, sortBy: $sortBy,
    approvedOnStartDate: $approvedOnStartDate, approvedOnEndDate: $approvedOnEndDate, salesRepresentative: $salesRepresentative) {
        nodes {
            accountId
            createdOn
            additionalData
            businessProfile {
                salesRepresentative
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                beneficialOwners {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                }
                businessEmail
                controlPersons {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                    phoneDetails {
                      countryDialingCode
                      phoneNumber
                      type
                  }
                }
                dbaName
                establishedDate
                fundingAccount
                kybEvaluations {
                  id
                  entityId
                  flowCode
                  hostCode
                  evaluationStatus
                  evaluationDateTime
                  description
                  lastStatusChangedOn
                  lastStatusChangeComments
                  pendingDocuments {
                    type
                    isMandatory
                  }
                  hostEvaluationStatus
                  hostEvaluationResponse
                }
                kybStatus
                legalName
                originCountry
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
                taxIdNumber
                websiteAddress
                businessNature
                kybData {
                  description
                  documentExtension
                  documentId
                  documentName
                  documentSize
                  expiryDate
                  issuanceCountry
                  issuanceDate
                  maskedIssuanceId
                  type
              }
            }
            id
            partnerId
            status
            statusName
            type
        }
        pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            limit
            offset
            startCursor
            totalPages
            totalRecords
        }
    }
}`,
    {
      limit: pageSize,
      offset: pageNumber,
      partnerId: partnerId,
      status: status,
      legalName,
      createdOnStartDate: createdOnStartDate ? moment(createdOnStartDate).format("YYYY-MM-DD") : "",
      createdOnEndDate: createdOnEndDate ? moment(createdOnEndDate).format("YYYY-MM-DD") : "",
      approvedOnStartDate: approvedOnStartDate
        ? moment(approvedOnStartDate).format("YYYY-MM-DD")
        : "",
      approvedOnEndDate: approvedOnEndDate ? moment(approvedOnEndDate).format("YYYY-MM-DD") : "",
      salesRepresentative: salesRepresentative ? salesRepresentative : "",
      sortBy,
    },
  );
};

export const fetchAffiliateAccount = (params: QueryAffiliateAccountDetailArgs) => {
  const affiliateId = params.affiliateId || getSelectedAffiliateId() || undefined;
  // Only send affiliateId when present — null/empty is rejected by B2C as AF001.
  const variables: Record<string, unknown> = { accountId: params.accountId };
  if (affiliateId) {
    variables.affiliateId = affiliateId;
  }

  return query(
    `query affiliateAccountDetail ($accountId: ID, $affiliateId: ID) {
            affiliateAccountDetail (accountId: $accountId, affiliateId: $affiliateId) {
                accountNo
                accountProxyNo
                accountType
                accountWallets {
                    accountId
                    availableBalance
                    currency
                    currentBalance
                    id
                    productWalletCode
                }
                aggregatedAvailableBalance {
                  amount
                  currency
                }
                aggregatedCurrentBalance {
                    amount
                    currency
                }
                cards {
                    accountId
                    cardExpiry
                    consumerId
                    fulfillment {
                        isBulkShipment
                        patternCode
                        pointOfContact {
                            countryDialingCode
                            name
                            phoneNumber
                        }
                        shippingAddress {
                            address1
                            address2
                            addressType
                            city
                            country
                            postalCode
                            state
                        }
                        shippingType
                    }
                    id
                    lastFour
                    printedConsumerName
                    productCode
                    replacementFor
                    replacementReason
                    shippingDate
                    shippingStatus
                    status
                }
                consumerId
                ddaNumber
                id
                productCode
                routingNumber
                status
            }
        }`,
    variables,
  );
};

export const fetchProducts = (affiliateId: string, brandType?: string) => {
  const id = affiliateId || getSelectedAffiliateId() || undefined;
  return query(
    `query products ($affiliateId: ID!, $brandType: String) {
      products (affiliateId: $affiliateId, brandType: $brandType) {
          brandCode
          brandName
          brandCategory
          id
      }
  }`,
    { affiliateId: id, brandType: brandType || "" },
  );
};

export const fetchNonPersonalisedCards = (orderProps: any) => {
  const { pageSize, pageNumber, enrollmentBatchId, affiliateId } = orderProps;
  const finalAffiliateId = affiliateId || getSelectedAffiliateId() || undefined;

  return query(
    `
        query batchCards ($enrollmentBatchId: String!, $affiliateId: String, $limit: Int, $offset: Int) {
            batchCards (enrollmentBatchId: $enrollmentBatchId, affiliateId: $affiliateId, limit: $limit, offset: $offset) {
                edges {
                    cursor
                    node {
                        accountId
                        cardExpiry
                        fulfillment {
                            patternCode
                            pointOfContact {
                                countryDialingCode
                                name
                                phoneNumber
                            }
                            shippingAddress {
                                address1
                                address2
                                addressType
                                city
                                country
                                postalCode
                                state
                            }
                            shippingType
                        }
                        id
                        lastFour
                        printedConsumerName
                        productCode
                        replacementFor
                        replacementReason
                        status
                    }
                }
                nodes {
                    accountId
                    cardExpiry
                    fulfillment {
                        patternCode
                        pointOfContact {
                            countryDialingCode
                            name
                            phoneNumber
                        }
                        shippingAddress {
                            address1
                            address2
                            addressType
                            city
                            country
                            postalCode
                            state
                        }
                        shippingType
                    }
                    id
                    lastFour
                    printedConsumerName
                    productCode
                    replacementFor
                    replacementReason
                    status
                }
                pageInfo {
                    endCursor
                    hasNextPage
                    hasPreviousPage
                    limit
                    offset
                    startCursor
                    totalPages
                    totalRecords
                }
            }
        }
    
    
    `,
    {
      enrollmentBatchId: enrollmentBatchId,
      affiliateId: finalAffiliateId,
      limit: pageSize,
      offset: pageNumber,
    },
  );
};

export const fetchConsumerTransactions = (transactionProps: QueryTransactionsArgs) => {
  const {
    requestType = [],
    accountId = "",
    affiliateId = "",
    consumerId = "",
    fromDate = "",
    toDate = "",
    limit,
    offset,
  } = transactionProps;

  const finalAffiliateId = affiliateId || getSelectedAffiliateId() || undefined;

  return query(
    `
    query transactions ($accountId: String, $affiliateId: String, $consumerId: String, $fromDate: String, $toDate: String $requestType: [String!], $limit: Int, $offset: Int) {
        transactions (accountId: $accountId, affiliateId: $affiliateId, consumerId: $consumerId, fromDate: $fromDate, toDate: $toDate, requestType: $requestType, limit: $limit, offset: $offset) {
            edges {
                cursor
                node {
                    accountId
                    accountWalletId
                    approvedAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    authExpireOn
                    authId
                    availableBalance {
                        amount
                        currency
                    }
                    cardId
                    cashbackAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    consumerId
                    currentBalance {
                        amount
                        currency
                    }
                    description
                    entity {
                        id
                        type
                    }
                    externalRefNo
                    feeAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    id
                    interfaceType
                    isAdvice
                    merchant {
                        city
                        country
                        group
                        name
                        postalCode
                        state
                        type
                    }
                    messageType
                    originalTransactionId
                    partialApproved
                    paymentNetwork
                    productCode
                    requestType
                    resultCode
                    resultDetailCode
                    retrievalReferenceNo
                    surchargeAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    transBatchId
                    transactionAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    transactionDatetime
                    transactionStatus
                    transactionType
                }
            }
            nodes {
                accountId
                accountWalletId
                approvedAmount {
                    amount
                    currency
                    exchangeRate
                }
                authExpireOn
                authId
                availableBalance {
                    amount
                    currency
                }
                cardId
                cardLastFour
                cashbackAmount {
                    amount
                    currency
                    exchangeRate
                }
                consumerId
                currentBalance {
                    amount
                    currency
                }
                description
                entity {
                    id
                    type
                }
                externalRefNo
                feeAmount {
                    amount
                    currency
                    exchangeRate
                }
                id
                interfaceType
                isAdvice
                merchant {
                    city
                    country
                    group
                    name
                    postalCode
                    state
                    type
                }
                messageType
                originalTransactionId
                partialApproved
                paymentNetwork
                productCode
                requestType
                resultCode
                resultDetailCode
                retrievalReferenceNo
                surchargeAmount {
                    amount
                    currency
                    exchangeRate
                }
                transBatchId
                transactionAmount {
                    amount
                    currency
                    exchangeRate
                }
                transactionDatetime
                transactionStatus
                transactionType
                requestTypeDescription
                transTypeDescription
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                limit
                offset
                startCursor
                totalPages
                totalRecords
            }
        }
    }`,
    {
      requestType,
      consumerId,
      affiliateId: finalAffiliateId,
      accountId,
      fromDate,
      toDate,
      limit,
      offset,
    },
  );
};

export const changeCardStatus = (statusProps: any) => {
  return mutation(
    `mutation updateCardStatus ($cardId: String!, $reason: String, $status: String!) {
          updateCardStatus (cardId: $cardId, reason: $reason, status: $status) {
              success
          }
      }`,
    { ...statusProps },
  );
};

export const createKycEvaluation = (kycProps: any) => {
  return mutation(
    `mutation createKycEvaluation ($kycEntityId: String!, $kycFlowId: String!,) {
    createKycEvaluation (kycEntityId: $kycEntityId,  kycFlowId: $kycFlowId) {
        kycEvaluation {
           id
           kycFlowId
           kycDateTime
           status
           kycEntity {
            id
            firstName
            lastName
            gender
            email
            dateOfBirth
            citizenshipCountryCode
            kycData {
                type
                issuanceCountry
                issuanceDate
                expiryDate
                maskedIssuanceId
            }
            phoneDetails {
                type
                countryDialingCode
                phoneNumber
            }
            addresses {
                addressType
                address1
                address2
                city
                state
                postalCode
                country
            }
            extRefNo
           }
           pendingDocs
        }
    }
}`,
    { ...kycProps },
  );
};

export const fetchAllConsumers = (consumerProps: any) => {
  const { pageSize, pageNumber, affiliateId, ...restFilters } = consumerProps;
  const finalAffiliateId = affiliateId || getSelectedAffiliateId();

  return query(
    `query consumers ($affiliateId: String, $brandCode: String, $ddaNumber: String,  $externalRefNo: String, $email: String, $firstName: String, $lastName: String, $limit: Int, $offset: Int, $partnerId: String, $kycStatus: String) {
        consumers (affiliateId: $affiliateId, brandCode: $brandCode, ddaNumber: $ddaNumber,externalRefNo: $externalRefNo, email: $email, firstName: $firstName, lastName: $lastName, limit: $limit, offset: $offset, partnerId: $partnerId, kycStatus: $kycStatus) {
            edges {
                cursor
                node {
                    accounts {
                        accountNo
                        accountProxyNo
                        accountType
                        accountWallets {
                            accountId
                            availableBalance
                            currency
                            currentBalance
                            id
                            productWalletCode
                        }
                        aggregatedAvailableBalance {
                            amount
                            currency
                        }
                        aggregatedCurrentBalance {
                            amount
                            currency
                        }
                        consumerId
                        ddaNumber
                        id
                        productCode
                        routingNumber
                        status
                    }
                    addresses {
                        address1
                        address2
                        addressType
                        city
                        country
                        postalCode
                        state
                    }
                    affiliateId
                    brandCode
                    cards {
                        accountId
                        cardExpiry
                        consumerId
                        fulfillment {
                            patternCode
                            shippingType
                        }
                        id
                        lastFour
                        printedConsumerName
                        productCode
                        replacementFor
                        replacementReason
                        status
                    }
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    enrolmentDate
                    externalRefNo
                    firstName
                    gender
                    id
                    kycData {
                        expiryDate
                        issuanceCountry
                        issuanceDate
                        maskedIssuanceId
                        type
                    }
                    kycResults {
                        docReviewStatus
                        entityId
                        flowId
                        kycBad
                        kycGood
                        kycResultId
                        lastKycDatetime
                        status
                    }
                    kycStatus
                    lastName
                    middleName
                    partnerId
                    phoneDetails {
                        countryDialingCode
                        phoneNumber
                        type
                    }
                }
            }
            nodes {
                accounts {
                    accountNo
                    accountProxyNo
                    accountType
                    accountWallets {
                        accountId
                        availableBalance
                        currency
                        currentBalance
                        id
                        productWalletCode
                    }
                    aggregatedAvailableBalance {
                        amount
                        currency
                    }
                    aggregatedCurrentBalance {
                        amount
                        currency
                    }
                    cards {
                        accountId
                        cardExpiry
                        consumerId
                        fulfillment {
                            patternCode
                            shippingType
                        }
                        id
                        lastFour
                        printedConsumerName
                        productCode
                        replacementFor
                        replacementReason
                        status
                    }
                    consumerId
                    ddaNumber
                    id
                    productCode
                    routingNumber
                    status
                }
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                affiliateId
                brandCode
                cards {
                    accountId
                    cardExpiry
                    consumerId
                    fulfillment {
                        patternCode
                        pointOfContact {
                            countryDialingCode
                            name
                            phoneNumber
                        }
                        shippingAddress {
                            address1
                            address2
                            addressType
                            city
                            country
                            postalCode
                            state
                        }
                        shippingType
                    }
                    id
                    lastFour
                    printedConsumerName
                    productCode
                    replacementFor
                    replacementReason
                    status
                }
                citizenshipCountryCode
                dateOfBirth
                email
                enrolmentDate
                externalRefNo
                firstName
                gender
                id
                kycData {
                    expiryDate
                    issuanceCountry
                    issuanceDate
                    maskedIssuanceId
                    type
                }
                kycResults {
                    docReviewStatus
                    entityId
                    flowId
                    kycBad
                    kycGood
                    kycResultId
                    lastKycDatetime
                    status
                }
                kycStatus
                lastName
                middleName
                partnerId
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                limit
                offset
                startCursor
                totalPages
                totalRecords
            }
        }
    }`,
    {
      limit: pageSize,
      offset: pageNumber,
      affiliateId: finalAffiliateId,
      ...restFilters,
    },
  );
};

export const fetchKycDetails = (entityId: string) => {
  return query(
    `query kycDetails ($entityId: String!) {
            kycDetails (entityId: $entityId) {
                kycDocs {
                    approvalStatus
                    description
                    documentId
                    extension
                    kycEntityId
                    kycEvaluationId
                    kycFlowId
                    label
                    page
                    type
                    uploadStatus
                    uploadedDateTime
                }
                kycEntityId
                kycEvaluationId
                kycEvaluations {
                    id
                    kycDatetime
                    kycEntityId
                    kycFlowId
                    kycVendorResponses {
                        code
                        description
                        kycVendor
                        type
                    }
                    status
                }
                kycFlowId
                kycStatus
                lastKycDatetime
            }
        }`,
    {
      entityId,
    },
  );
};

export const fetchAffilate = (affiliateId: string) => {
  return query(
    `query affiliate ($id: String!) {
    affiliate (id: $id) {
        accountId
        additionalData
        businessProfile {
            salesRepresentative
            kybEvaluations {
                id
                entityId
                flowCode
                hostCode
                evaluationStatus
                evaluationDateTime
                description
                lastStatusChangedOn
                lastStatusChangeComments
                pendingDocuments {
                  type
                  isMandatory
                }
                hostEvaluationStatus
                hostEvaluationResponse
            }
            addresses {
                address1
                address2
                addressType
                city
                country
                postalCode
                state 
            }
            beneficialOwners {
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                citizenshipCountryCode
                dateOfBirth
                email
                firstName
                gender
                id
                kycData {
                    expiryDate
                    issuanceCountry
                    issuanceDate
                    maskedIssuanceId
                    type
                }
                lastName
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
            }
            businessEmail
            controlPersons {
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                citizenshipCountryCode
                dateOfBirth
                email
                firstName
                gender
                id
                kycData {
                    expiryDate
                    issuanceCountry
                    issuanceDate
                    maskedIssuanceId
                    type
                }
                lastName
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
            }
            dbaName
            establishedDate
            fundingAccount
            kybData {
                expiryDate
                issuanceCountry
                issuanceDate
                maskedIssuanceId
                type
            }
            kybResults {
                docReviewStatus
                entityId
                flowId
                kycBad
                kycGood
                kycResultId
                lastKycDatetime
                status
            }
            kybStatus
            legalName
            employmentCategory
            originCountry
            phoneDetails {
                countryDialingCode
                phoneNumber
                type
            }
            taxIdNumber
            websiteAddress
            businessNature
            kybData {
              description
              documentExtension
              documentId
              documentName
              documentSize
              expiryDate
              issuanceCountry
              issuanceDate
              maskedIssuanceId
              type
          }
        }
        id
        partnerId
        status
        statusName
        type
    }
}`,
    {
      id: affiliateId,
    },
  );
};
export const fetchKybDocuments = (entityId: string) => {
  return query(
    `query kybDocuments ($entityId: String!) {
      kybDocuments (entityId: $entityId) {
          additionalData
          entityId
          expirationDate
          extension
          externalRefNo
          id
          isMandatory
          issuanceCountry
          issuanceDate
          name
          type
      }
  }`,
    {
      entityId,
    },
  );
};

export const fetchKycDocument = (docProps: any) => {
  const { kycEntityId, documentId, kycFlowId } = docProps;
  return query(
    `query kycDocument ($entityId: String!, $kycDocumentId: String!, $kycFlowId: String!) {
            kycDocument (entityId: $entityId, kycDocumentId: $kycDocumentId, kycFlowId: $kycFlowId)
        }`,
    {
      entityId: kycEntityId,
      kycDocumentId: documentId,
      kycFlowId: kycFlowId,
    },
  );
};

export const fetchNotes = (notesProps: any) => {
  const { entityId, entityType, limit, offset } = notesProps;
  return query(
    `query notes ($entityId: String!, $entityType: String!, $limit: Int, $offset: Int) {
            notes (entityId: $entityId, entityType: $entityType, limit: $limit, offset: $offset) {
                nodes {
                    agentCode
                    agentName
                    createdOn
                    appCode
                    channel
                    entity {
                        id
                        type
                    }
                    id
                    text
                    type
                }
                pageInfo {
                    endCursor
                    hasNextPage
                    hasPreviousPage
                    limit
                    offset
                    startCursor
                    totalPages
                    totalRecords
                }
            }
        }`,
    {
      entityId: entityId,
      entityType: entityType,
      limit: limit,
      offset: offset,
    },
  );
};

export const addNotes = (notesProps: any) => {
  return mutation(
    `mutation addNotes ($appCode: String, $author: String, $channel: String!, $comments: String!, $entityId: ID!, $entityType: String!, $type: String!) {
        addNotes (appCode: $appCode, author: $author, channel: $channel, comments: $comments, entityId: $entityId, entityType: $entityType, type: $type) {
            note {
                agentCode
                agentName
                appCode
                channel
                entity {
                    id
                    type
                }
                id
                text
                type
                createdOn
            }
        }
    }`,
    {
      ...notesProps,
    },
  );
};

export const addKycReview = (affiliateProps: any) => {
  return mutation(
    `mutation addKycReview ($entityId: String!, $kycFlowId: String!, $notes: [String!], $reason: String, $status: String!) {
        addKycReview (entityId: $entityId, kycFlowId: $kycFlowId, notes: $notes, reason: $reason, status: $status) {
            kycReview {
                id
                kycEntityId
                kycFlowId
                notes {
                    note
                    noteAuthor
                }
                reason
                reviewDatetime
                reviewer
                status
            }
        }
    }`,
    {
      ...affiliateProps,
    },
  );
};

export const fetchOrders = (orderProps: any) => {
  const {
    pageSize,
    pageNumber,
    productId,
    partnerId,
    affiliateId,
    orderId,
    batchStatus,
    fromDate,
    toDate,
    enrollmentBatchType,
  } = orderProps;

  const finalAffiliateId = affiliateId || getSelectedAffiliateId();

  return query(
    `
    query cardOrders ($fromDate: String, $toDate: String,$affiliateId: String, $batchId: String, $batchStatus: String, $brandCode: String, $enrollmentBatchType: String, $externalRefNo: String, $limit: Int, $offset: Int, $partnerId: String) {
        cardOrders ( fromDate: $fromDate, toDate: $toDate, affiliateId: $affiliateId, batchId: $batchId, batchStatus: $batchStatus brandCode: $brandCode, enrollmentBatchType: $enrollmentBatchType, externalRefNo: $externalRefNo, limit: $limit, offset: $offset, partnerId: $partnerId) {
            edges {
                cursor
                node {
                    acceptedCount
                    affiliateId
                    batchId
                    batchProcessedAt
                    batchRequestedAt
                    brandCode
                    cancelledCount
                    confirmedOn
                    declinedCount
                    denomination {
                        loadAmount {
                            amount
                            currency
                            exchangeRate
                        }
                        loadEvent
                    }
                    enrollmentIds {
                        accountId
                        cardId
                        ddaNumber
                        routingNumber
                    }
                    externalRefNo
                    fulfillment {
                        patternCode
                        pointOfContact {
                            countryDialingCode
                            name
                            phoneNumber
                        }
                        shippingAddress {
                            address1
                            address2
                            addressType
                            city
                            country
                            postalCode
                            state
                        }
                        shippingType
                    }
                    fundingSource {
                        id
                        type
                    }
                    partnerId
                    pointOfContact {
                        countryDialingCode
                        name
                        phoneNumber
                    }
                    printedConsumerName
                    printedExtraLine
                    processedCount
                    processedOn
                    requestedCount
                    status
                    type
                }
            }
            nodes {
                acceptedCount
                affiliateId
                batchId
                batchProcessedAt
                batchRequestedAt
                brandCode
                cancelledCount
                confirmedOn
                declinedCount
                denomination {
                    loadAmount {
                        amount
                        currency
                        exchangeRate
                    }
                    loadEvent
                }
                enrollmentIds {
                    accountId
                    cardId
                    ddaNumber
                    routingNumber
                }
                externalRefNo
                fulfillment {
                    patternCode
                    pointOfContact {
                        countryDialingCode
                        name
                        phoneNumber
                    }
                    shippingAddress {
                        address1
                        address2
                        addressType
                        city
                        country
                        postalCode
                        state
                    }
                    shippingType
                }
                fundingSource {
                    id
                    type
                }
                partnerId
                pointOfContact {
                    countryDialingCode
                    name
                    phoneNumber
                }
                printedConsumerName
                printedExtraLine
                processedCount
                processedOn
                requestedCount
                status
                type
                additionalData
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                limit
                offset
                startCursor
                totalPages
                totalRecords
            }
        }
    }`,
    {
      limit: pageSize,
      offset: pageNumber,
      brandCode: productId,
      partnerId,
      affiliateId: finalAffiliateId,
      batchId: orderId,
      batchStatus,
      enrollmentBatchType,
      fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : "",
      toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : "",
    },
  );
};

export const createNonPersonalizedBatch = (batchProps: any) => {
  const { affiliateId, ...rest } = batchProps;
  const finalAffiliateId = affiliateId || getSelectedAffiliateId();
  return mutation(
    `mutation bulkNonPersonalized ($productCode: String!, $affiliateId: String, $fulfillment: FulfillmentInput!, $denomination: DenominationInput, $fundingSource: FundingSourceInput, $requestedCount: Int!, $printedConsumerName: String, $printedExtraLine: String, $additionalData: JSON) {
      bulkNonPersonalized (productCode: $productCode, affiliateId: $affiliateId, fulfillment: $fulfillment, denomination: $denomination, fundingSource: $fundingSource, requestedCount: $requestedCount, printedConsumerName: $printedConsumerName, printedExtraLine: $printedExtraLine, additionalData: $additionalData) {
          nonPersonalizeBatch {
              batchId
          }
      }
  }`,
    { ...rest, affiliateId: finalAffiliateId },
  );
};

export const createPersonalizedBatch = (batchProps: any) => {
  const { affiliateId, ...rest } = batchProps;
  const finalAffiliateId = affiliateId || getSelectedAffiliateId();
  return mutation(
    `mutation bulkPersonalized ($brandCode: String!,  $affiliateId: String, $fulfillment: FulfillmentInput, $additionalData: JSON) {
      bulkPersonalized (brandCode: $brandCode,affiliateId: $affiliateId, fulfillment: $fulfillment, additionalData: $additionalData) {
          personalizeBatch {             
              batchId
          }
      }
  }`,
    { ...rest, affiliateId: finalAffiliateId },
  );
};

export const batchEnrollments = (batchId: any, enrollments: any) => {
  return mutation(
    `mutation batchEnrollmentCollection ($batchId: String!, $enrollments: [BatchEnrollmentInput!]!) {
      batchEnrollmentCollection (batchId: $batchId, enrollments: $enrollments) {
          batchEnrollment {
              addresses {
                  address1
                  address2
                  addressType
                  city
                  country
                  postalCode
                  state
              }
              batchId
              citizenshipCountryCode
              dateOfBirth
              email
              enrollmentStatus
              externalRefNo
              firstName
              gender
              additionalData
              id
              kycData {
                  expiryDate
                  issuanceCountry
                  issuanceDate
                  maskedIssuanceId
                  type
              }
              lastName
              middleName
              phoneDetails {
                  countryDialingCode
                  phoneNumber
                  type
              }
          }
      }
  }`,
    { batchId, enrollments },
  );
};

export const confirmBatch = (batchId: string) => {
  return mutation(
    `mutation confirmBatch ($batchId: String!) {
      confirmBatch (batchId: $batchId) {
          batch {
              batchId
          }
      }
  }`,
    { batchId },
  );
};

export const affiliateSignup = (formData: any) => {
  return mutation(
    `mutation affiliateSignup ($address: [AddressInput!]!, $companyEmail: String!, $controlPersons: [ControlPersonInput!]!, $dbaName: String!, $establishedDate: String!, $kybData: [KycDataInput!], $legalEntityName: String!, $phoneDetails: [PhoneDetailInput!]!, $taxIdNumber: String!, $userData: UserDataInput!, $websiteAddress: String) {
            affiliateSignup (address: $address, companyEmail: $companyEmail, controlPersons: $controlPersons, dbaName: $dbaName, establishedDate: $establishedDate, kybData: $kybData, legalEntityName: $legalEntityName, phoneDetails: $phoneDetails, taxIdNumber: $taxIdNumber, userData: $userData, websiteAddress: $websiteAddress) {
                affiliate {
                    accountId
                    businessProfile {
                        addresses {
                            address1
                            address2
                            addressType
                            city
                            country
                            postalCode
                            state
                        }
                        beneficialOwners {
                            citizenshipCountryCode
                            dateOfBirth
                            email
                            firstName
                            gender
                            id
                            lastName
                        }
                        businessEmail
                        controlPersons {
                            citizenshipCountryCode
                            dateOfBirth
                            email
                            firstName
                            gender
                            id
                            lastName
                        }
                        dbaName
                        establishedDate
                        fundingAccount
                        kybEvaluations {
                          id
                          entityId
                          flowCode
                          hostCode
                          evaluationStatus
                          evaluationDateTime
                          description
                          lastStatusChangedOn
                          lastStatusChangeComments
                          pendingDocuments {
                            type
                            isMandatory
                          }
                          hostEvaluationStatus
                          hostEvaluationResponse
                        }
                        kybStatus
                        legalName
                        originCountry
                        phoneDetails {
                            countryDialingCode
                            phoneNumber
                            type
                        }
                        websiteAddress
                    }
                    id
                    partnerId
                    type
                }
            }
        }`,
    { ...formData },
  );
};

export const addAffiliate = (formData: any) => {
  return mutation(
    `mutation addAffiliate ($address: [AddressInput!]!, $companyEmail: String!, $controlPersons: [ControlPersonInput!]!, $dbaName: String!, $establishedDate: String!, $kybData: [KycDataInput!], $legalEntityName: String!, $phoneDetails: [PhoneDetailInput!]!, $taxIdNumber: String!, $userData: UserDataInput!, $websiteAddress: String, $employmentCategory: String, $businessNature: String, $additionalData: JSON) {
        addAffiliate (address: $address, companyEmail: $companyEmail, controlPersons: $controlPersons, dbaName: $dbaName, establishedDate: $establishedDate, kybData: $kybData, legalEntityName: $legalEntityName, phoneDetails: $phoneDetails, taxIdNumber: $taxIdNumber, userData: $userData, websiteAddress: $websiteAddress, employmentCategory: $employmentCategory, businessNature: $businessNature, additionalData: $additionalData) {
            affiliate {
                accountId
                additionalData
                businessProfile {
                    addresses {
                        address1
                        address2
                        addressType
                        city
                        country
                        postalCode
                        state
                    }
                    beneficialOwners {
                        citizenshipCountryCode
                        dateOfBirth
                        email
                        firstName
                        gender
                        id
                        lastName
                    }
                    businessEmail
                    controlPersons {
                        citizenshipCountryCode
                        dateOfBirth
                        email
                        firstName
                        gender
                        id
                        lastName
                    }
                    dbaName
                    establishedDate
                    fundingAccount
                    kybData {
                        expiryDate
                        issuanceCountry
                        issuanceDate
                        maskedIssuanceId
                        type
                    }
                    kybResults {
                        docReviewStatus
                        entityId
                        flowId
                        kycBad
                        kycGood
                        kycResultId
                        lastKycDatetime
                        status
                    }
                    kybEvaluations {
                        description
                        entityId
                        evaluationDateTime
                        evaluationStatus
                        flowCode
                        hostCode
                        hostEvaluationResponse
                        hostEvaluationStatus
                        id
                        lastStatusChangeComments
                        lastStatusChangedOn
                    }
                    kybStatus
                    legalName
                    employmentCategory
                    originCountry
                    phoneDetails {
                        countryDialingCode
                        phoneNumber
                        type
                    }
                    taxIdNumber
                    websiteAddress
                    businessNature
                }
                id
                partnerId
                type
            }
        }
    }`,
    { ...formData },
  );
};

export type UserType = AddUserInput;

export type { UserEntityInput };

export const addUser = (user: UserType) => {
  const { email, entities, entityRoleCode, firstName, lastName, mobileNo } = user;

  return mutation(
    `mutation addUser ($email: String!, $entities: [UserEntityInput!], $entityRoleCode: String, $firstName: String!, $lastName: String!, $mobileNo: String!) {
      addUser (email: $email, entities: $entities, entityRoleCode: $entityRoleCode, firstName: $firstName, lastName: $lastName, mobileNo: $mobileNo) {
        success
      }
    }`,
    {
      email,
      entities: entities && entities.length > 0 ? entities : null,
      entityRoleCode: entityRoleCode ?? null,
      firstName,
      lastName,
      mobileNo,
    },
  );
};

export const updateUser = (input: UpdateUserMutationInput) => {
  const { userId, appRoleCode, entities, mobileNo, firstName, lastName } = input;

  return mutation(
    `mutation updateUser ($appRoleCode: String!, $entities: [UserEntityInput!], $firstName: String, $lastName: String, $mobileNo: String, $userId: ID!) {
      updateUser (appRoleCode: $appRoleCode, entities: $entities, firstName: $firstName, lastName: $lastName, mobileNo: $mobileNo, userId: $userId) {
        success
        user {
          appRoleCode
          appRoleTitle
          authId
          createdAt
          email
          firstName
          id
          isActive
          isPasswordSet
          lastLoginDate
          lastName
          mobileNo
        }
      }
    }`,
    {
      userId,
      appRoleCode,
      entities: entities && entities.length > 0 ? entities : undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      mobileNo: mobileNo ?? null,
    },
  );
};

export const updateUserStatus = (id: number) => {
  return mutation(
    `mutation toggleUserStatus ($id: ID!) {
      toggleUserStatus (id: $id) {
        success
      }
    }`,
    { id },
  );
};

export const resendUserInvite = (id: number) => {
  return mutation(
    `mutation resendUserInvite ($id: ID) {
      resendUserInvite (id: $id) {
        success
      }
    }`,
    { id },
  );
};

export const fetchAllUsers = (userProps: any) => {
  const { pageSize, pageNumber, affiliateId, firstName, lastName, email, salesRepresentative } =
    userProps;

  const finalAffiliateId = affiliateId || getSelectedAffiliateId();

  return query(
    `query users ($affiliateId: String, $partnerId: String, $firstName: String, $lastName: String, $email: String, $limit: Int, $offset: Int, $salesRepresentative: Boolean) {
      users (affiliateId: $affiliateId, limit: $limit, offset: $offset, partnerId: $partnerId, firstName: $firstName, lastName: $lastName, email: $email, salesRepresentative: $salesRepresentative) {
        nodes {
          appRoleCode
          appRoleTitle
          authId
          createdAt
          email
          firstName
          id
          isActive
          isPasswordSet
          lastLoginDate
          lastName
          mobileNo
          affiliates
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          limit
          offset
          startCursor
          totalPages
          totalRecords
        }
      }
    }`,
    {
      affiliateId: finalAffiliateId,
      firstName,
      lastName,
      email,
      limit: pageSize,
      offset: pageNumber,
      salesRepresentative: salesRepresentative,
    },
  );
};

export type ReissueCardType = {
  cardId: string;
  cardShippingMethod: string;
  issueType: string;
  isNewCard: boolean;
  replacementCardEntityId?: string;
  replacementCardEntityType?: string;
};

export const reissueCard = (reissueCard: ReissueCardType) => {
  return mutation(
    `mutation reissueCard ($cardId: ID!, $cardShippingMethod: String, $isNewCard: Boolean!, $issueType: String, $replacementCardEntityId: String, $replacementCardEntityType: String) {
      reissueCard (cardId: $cardId, cardShippingMethod: $cardShippingMethod, isNewCard: $isNewCard, issueType: $issueType, replacementCardEntityId: $replacementCardEntityId, replacementCardEntityType: $replacementCardEntityType) {
          success
      }
    }`,
    { ...reissueCard },
  );
};

export type AssociateCardType = {
  cardEntityId: string;
  cardEntityType: string;
  entityId: string;
  entityType: string;
};

export const associateCard = (reissueCard: AssociateCardType) => {
  return mutation(
    `mutation associateCard ($cardEntityId: String!, $cardEntityType: String!, $entityId: String!, $entityType: String!) {
      associateCard (cardEntityId: $cardEntityId, cardEntityType: $cardEntityType, entityId: $entityId, entityType: $entityType) {
          success
      }
    }`,
    { ...reissueCard },
  );
};

export const fetchBatchConsumers = (consumerProps: any) => {
  const { pageSize, pageNumber, batchId, enrollmentStatus, affiliateId } = consumerProps;
  const finalAffiliateId = affiliateId || getSelectedAffiliateId() || undefined;

  return query(
    `query batchEnrollments ($batchId: String!, $affiliateId: String, $enrollmentStatus: String, $firstName: String, $lastName: String, $email: String, $dateOfBirth: String, $externalRefNo: String){
        batchEnrollments (batchId: $batchId, affiliateId: $affiliateId, enrollmentStatus: $enrollmentStatus, firstName: $firstName, lastName: $lastName, email: $email, dateOfBirth: $dateOfBirth, externalRefNo: $externalRefNo) {
           nodes {
            batchId
            id
            firstName
            middleName
            lastName
            gender
            email
            additionalData
            dateOfBirth
            citizenshipCountryCode
            externalRefNo
            enrollmentStatus
            description
         addresses {
            addressType
            address1
            address2
            city
            state
            postalCode
            country
           }
         phoneDetails {
            type
            countryDialingCode
            phoneNumber
        }
         kycData {
            type
            issuanceCountry
            issuanceDate
            expiryDate
            maskedIssuanceId
        }
       }
       pageInfo {
             endCursor
             hasNextPage
             hasPreviousPage
             limit
             offset
             startCursor
             totalPages
             totalRecords
           }
        }
   }`,
    {
      enrollmentStatus: enrollmentStatus,
      batchId: batchId,
      affiliateId: finalAffiliateId,
      limit: pageSize,
      offset: pageNumber,
    },
  );
};

export const fetchDashboardList = (metabaseProps: { widgetId: number | null }) => {
  const { widgetId } = metabaseProps;

  return query(
    `query metabaseUrls ($widgetId: Int) {
        metabaseUrls (widgetId: $widgetId)
      }`,
    { widgetId: widgetId },
  );
};

export const fetchDefaultDashboard = () => {
  return query(
    `query defaultMetabaseUrl {
        defaultMetabaseUrl
      }`,
  );
};

export const uploadKycDocuments = (formData: any) => {
  return mutation(
    `mutation uploadKycDocuments($documents: [KycDocumentInput!]!, $kycEntityId: String!, $kycFlowId: String!) { 
      uploadKycDocuments(documents: $documents, kycEntityId: $kycEntityId, kycFlowId: $kycFlowId) { 
        documents { 
          uploadStatus  
        } 
      } 
    }`,
    { ...formData },
  );
};

export const uploadKybDocuments = (formData: any) => {
  return mutation(
    `mutation uploadKybDocuments($documents: [KybDocumentInput!]!, $entityId: String!) { 
      uploadKybDocuments(documents: $documents, entityId: $entityId ) { 
        documents { 
          entityId
          expirationDate
          extension
          externalRefNo
          id
          isMandatory
          issuanceCountry
          issuanceDate
          name
          type
        } 
      } 
    }`,
    { ...formData },
  );
};

export const getConsumerFileFields = (code: string) => {
  return query(
    `query entityFields ($code: String!) {
      entityFields (code: $code) {
          code
          description
          name
          title
          visibleOrder
      }
    }`,
    { code },
  );
};

export const listConsumerFiles = ({ pageNumber, limit, status }: any) => {
  return query(
    `query consumerFiles ($limit: Int, $offset: Int, $status: String) {
      consumerFiles (limit: $limit, offset: $offset, status: $status) {
          nodes {
              createdOn
              description
              id
              name
              status
              updatedOn
          }
          pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              limit
              offset
              startCursor
              totalPages
              totalRecords
          }
      }
  }`,
    {
      limit,
      offset: pageNumber,
      status,
    },
  );
};

export const listTransactionFiles = ({ pageNumber, limit, status, affiliateId }: any) => {
  const finalAffiliateId = affiliateId || getSelectedAffiliateId();
  return query(
    `query transactionFiles ($affiliateId: String, $limit: Int, $offset: Int, $status: String) {
    transactionFiles (affiliateId: $affiliateId, limit: $limit, offset: $offset, status: $status) {
        edges {
            cursor
            node {
                createdOn
                description
                id
                name
                status
                updatedOn
            }
        }
        nodes {
            createdOn
            description
            id
            name
            status
            updatedOn
        }
        pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            limit
            offset
            startCursor
            totalPages
            totalRecords
        }
    }
}`,
    {
      affiliateId: finalAffiliateId,
      limit,
      offset: pageNumber,
      status,
    },
  );
};

export const updateTransferBatch = (updateBatchProps: UpdateBatchProps) => {
  const { batchId, note, noteType, status } = updateBatchProps;
  return query(
    `mutation updateTransferBatch ($batchId: String!, $note: String!, $noteType: String!, $status: String!) {
      updateTransferBatch (batchId: $batchId, note: $note, noteType: $noteType, status: $status) {
        transferBatch {
          additionalData
          affiliateId
          createdOn
          errorCount
          externalRefNo
          id
          partnerId
          payer {
            id
            type
          }
          pendingAmount
          pendingCount
          processedAmount
          processedCount
          requestType
          requestedAmount
          requestedAt
          requestedCount
          status
          transactionCurrency
          type
        }
      }
    }`,
    {
      batchId,
      note,
      noteType,
      status,
    },
  );
};

export const updateEnrollmentBatch = (updateBatchProps: UpdateBatchProps) => {
  const { batchId, note, noteType, status } = updateBatchProps;

  return query(
    `mutation updateEnrollmentBatch ($batchId: String!, $note: String!, $noteType: String!, $status: String!) {
      updateEnrollmentBatch (batchId: $batchId, note: $note, noteType: $noteType, status: $status) {
        batch {
          acceptedCount
          additionalData
          affiliateId
          batchId
          batchProcessedAt
          batchRequestedAt
          brandCode
          cancelledCount
          confirmedOn
          declinedCount
          denomination {
            loadAmount {
              amount
              currency
              exchangeRate
            }
            loadEvent
          }
          enrollmentIds {
            accountId
            cardId
            ddaNumber
            routingNumber
          }
          externalRefNo
          fulfillment {
            patternCode
            pointOfContact {
              countryDialingCode
              name
              phoneNumber
            }
            shippingAddress {
              address1
              address2
              addressType
              city
              country
              postalCode
              state
            }
            shippingType
          }
          fundingSource {
            id
            type
          }
          partnerId
          pointOfContact {
            countryDialingCode
            name
            phoneNumber
          }
          printedConsumerName
          printedExtraLine
          processedCount
          processedOn
          requestedCount
          status
          type
        }
      }
    }`,
    {
      batchId,
      note,
      noteType,
      status,
    },
  );
};

export const updateAffiliateDetail = (formData: any) => {
  return mutation(
    `mutation updateAffiliate ($additionalData: JSON, $affiliateId: String!, $businessProfile: BusinessProfileInput) {
      updateAffiliate (additionalData: $additionalData, affiliateId: $affiliateId, businessProfile: $businessProfile) {
          affiliate {
              accountId
              additionalData
              businessProfile {
                  addresses {
                      address1
                      address2
                      addressType
                      city
                      country
                      postalCode
                      state
                  }
                  beneficialOwners {
                      citizenshipCountryCode
                      dateOfBirth
                      email
                      firstName
                      gender
                      id
                      lastName
                  }
                  businessEmail
                  controlPersons {
                      citizenshipCountryCode
                      dateOfBirth
                      email
                      firstName
                      gender
                      id
                      lastName
                  }
                  dbaName
                  employmentCategory
                  establishedDate
                  fundingAccount
                  kybData {
                      description
                      documentExtension
                      documentId
                      documentName
                      documentSize
                      expiryDate
                      issuanceCountry
                      issuanceDate
                      maskedIssuanceId
                      type
                  }
                  kybResults {
                      docReviewStatus
                      entityId
                      flowId
                      kycBad
                      kycGood
                      kycResultId
                      lastKycDatetime
                      status
                  }
                  kybStatus
                  legalName
                  originCountry
                  phoneDetails {
                      countryDialingCode
                      phoneNumber
                      type
                  }
                  taxIdNumber
                  websiteAddress
                  businessNature
              }
              id
              partnerId
              type
          }
      }
  }`,
    { ...formData },
  );
};
export const fetchAffiliateStatusHistory = (
  affiliateStatusHistoryProps: AffiliateStatusHistoryProps,
) => {
  const { affiliateId, pageSize, pageNumber } = affiliateStatusHistoryProps;

  return query(
    `query affiliateStatusAuditTrails ($id: String!, $limit: Int, $offset: Int) {
      affiliateStatusAuditTrails (id: $id, limit: $limit, offset: $offset) {
        nodes {
          actionDatetime
          actionType
          applicationName
          applicationUrl
          clientIp
          fieldChangeLogs {
            status {
              newValue
              newValueName
              oldValue
              oldValueName
            }
          }
          userName
          userRole
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          limit
          offset
          startCursor
          totalPages
          totalRecords
        }
      }
    }`,
    {
      id: affiliateId,
      limit: pageSize,
      offset: pageNumber,
    },
  );
};

export const fetchLookupData = (lookupProps: any) => {
  const { lookupCodes } = lookupProps;
  return query(
    `query lookupData ($lookupCodes: [String!]!) {
      lookupData (lookupCodes: $lookupCodes) {
        lookupCode
        lookupTable {
          code
          description
          name
          title
          visibleOrder
        }
      }
    }`,
    {
      lookupCodes: lookupCodes,
    },
  );
};

export const updateAffiliateStatus = (props: updateAffiliateStatusProps) => {
  const { affiliateId, status, reason } = props;
  return mutation(
    `mutation updateAffiliateStatus ($id: String!, $reason: String, $statusCode: String!) {
      updateAffiliateStatus (id: $id, reason: $reason, statusCode: $statusCode) {
        affiliate {
          status
        }
      }
    }`,
    {
      id: affiliateId,
      statusCode: status,
      reason,
    },
  );
};

export const updateConsumerInformation = (props: updateConsumerPayload) => {
  const { addresses, consumerId, email, phoneDetails, externalRefNo } = props;
  return mutation(
    `mutation updateConsumer ($addresses: [AddressInput!], $email: String,  $externalRefNo: String, $id: String!, $phoneDetails: [PhoneDetailInput!]) {
      updateConsumer (addresses: $addresses, email: $email, externalRefNo: $externalRefNo, id: $id, phoneDetails: $phoneDetails) {
        consumer {
          addresses {
            address1
            address2
            addressType
            city
            country
            postalCode
            state
          }
          email
          id
          phoneDetails {
            countryDialingCode
            phoneNumber
            type
          }
          externalRefNo
        }
      }
    }`,
    {
      addresses,
      id: consumerId,
      email,
      externalRefNo,
      phoneDetails,
    },
  );
};

export const fetchConsumerDetails = (consumerId: string) => {
  return query(
    `query consumer ($id: String!) {
      consumer (id: $id) {
          accounts {
            accountNo
            accountProxyNo
            accountType
            accountWallets {
              accountId
              availableBalance
              currency
              currentBalance
              id
              productWalletCode
            }
            aggregatedAvailableBalance {
              amount
              currency
            }
            aggregatedCurrentBalance {
              amount
              currency
            }
            consumerId
            ddaNumber
            id
            productCode
            routingNumber
            status
          }
          addresses {
            address1
            address2
            addressType
            city
            country
            postalCode
            state
          }
          affiliateId
          brandCode
          cards {
            accountId
            cardExpiry
            consumerId
            fulfillment {
              patternCode
              pointOfContact {
                countryDialingCode
                name
                phoneNumber
              }
              shippingAddress {
                address1
                address2
                addressType
                city
                country
                postalCode
                state
              }
              shippingType
              shippedOn
              shipmentTrackingNo
            }
            id
            lastFour
            printedConsumerName
            creationDate
            activationDate
            productCode
            replacementFor
            replacementReason
            shippingStatus
            shippingDate
            status
          }
          citizenshipCountryCode
          dateOfBirth
          email
          enrolmentDate
          externalRefNo
          firstName
          gender
          id
          kycData {
            expiryDate
            issuanceCountry
            issuanceDate
            maskedIssuanceId
            type
          }
          kycResults {
            description
            docReviewStatus
            entityId
            flowId
            isManuallyApproved
            kycBad
            kycGood
            kycResultId
            lastKycDatetime
            pendingDocs
            status
          }
          kycStatus
          lastName
          middleName
          partnerId
          phoneDetails {
            countryDialingCode
            phoneNumber
            type
          }
        }
      }
    `,
    {
      id: consumerId,
    },
  );
};

export const fetchCurrentUserRolesInHierarchy = () => {
  return query(
    `
      query currentUserRolesInHierarchy {
        currentUserRolesInHierarchy {
          code
          title
          depth
          roleLevel
          entityCode
        }
      }
    `,
  );
};

export const fetchUsersAllowedAffiliateStatuses = (currentStatus: string) => {
  return query(
    `
      query allowedAffiliateStatusTransfers ($currentStatus: String!) {
        allowedAffiliateStatusTransfers (currentStatus: $currentStatus) {
          code
          description
          name
          title
          visibleOrder
        }
      }
    `,
    {
      currentStatus,
    },
  );
};

export const deleteDocument = (documentId: string) => {
  return mutation(
    `mutation deleteDocument ($id: String!) {
      deleteDocument (id: $id) {
          success
      }
    }`,
    { id: documentId },
  );
};

export const listAffiliateFiles = ({
  offset,
  limit,
  status,
  affiliateId,
}: QueryAffiliateFilesArgs) => {
  const finalAffiliateId = affiliateId || getSelectedAffiliateId();
  return query(
    `
      query affiliateFiles($affiliateId: String, $limit: Int, $offset: Int, $status: String) {
        affiliateFiles(affiliateId: $affiliateId, limit: $limit, offset: $offset, status: $status) {
  
          nodes {
            createdOn
            description
            id
            name
            status
            updatedOn
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            limit
            offset
            startCursor
            totalPages
            totalRecords
          }
        }
      }
    `,
    {
      affiliateId: finalAffiliateId,
      limit,
      offset: offset,
      status,
    },
  );
};

export const searchAccount = (accountProps: QueryAccountsArgs) => {
  const { accountId, affiliateId, aggregatedBalance, ddaNumber, entityId, entityType, status } =
    accountProps;

  const finalAffiliateId = affiliateId || getSelectedAffiliateId();

  return query(
    `query accounts ($accountId: String, $accountNo: String, $affiliateId: String, $aggregatedBalance: Boolean, $category: String, $ddaLastFour: String, $ddaNumber: String, $entityId: String, $entityType: String, $iban: String, $limit: Int, $offset: Int, $partnerId: String, $productCode: String, $status: String, $subCategory: String, $title: String, $type: String) {
        accounts (accountId: $accountId, accountNo: $accountNo, affiliateId: $affiliateId, aggregatedBalance: $aggregatedBalance, category: $category, ddaLastFour: $ddaLastFour, ddaNumber: $ddaNumber, entityId: $entityId, entityType: $entityType, iban: $iban, limit: $limit, offset: $offset, partnerId: $partnerId, productCode: $productCode, status: $status, subCategory: $subCategory, title: $title, type: $type) {
            nodes {
                accountNo
                accountProxyNo
                accountType
                accountWallets {
                    accountId
                    availableBalance
                    currency
                    currentBalance
                    id
                    productWalletCode
                }
                aggregatedAvailableBalance {
                    amount
                    currency
                }
                aggregatedCurrentBalance {
                    amount
                    currency
                }
                cards {
                    accountId
                    cardExpiry
                    consumerId
                    fulfillment {
                        isBulkShipment
                        patternCode
                        shippingType
                    }
                    id
                    lastFour
                    printedConsumerName
                    productCode
                    replacementFor
                    replacementReason
                    shippingDate
                    shippingStatus
                    status
                }
                consumerId
                ddaNumber
                id
                productCode
                routingNumber
                status
            }
            pageInfo {
                endCursor
                hasNextPage
                hasPreviousPage
                limit
                offset
                startCursor
                totalPages
                totalRecords
            }
        }
      }`,
    {
      accountId,
      affiliateId: finalAffiliateId,
      aggregatedBalance,
      ddaNumber,
      entityId,
      entityType,
      status,
    },
  );
};

export const putAffiliateDetail = (formData: any) => {
  const { additionalData, affiliateId, businessProfile } = formData;

  return mutation(
    `mutation putAffiliate ($additionalData: JSON, $affiliateId: String!, $businessProfile: BusinessProfileInput) {
      putAffiliate (additionalData: $additionalData, affiliateId: $affiliateId, businessProfile: $businessProfile) {
        affiliate {
            accountId
            additionalData
            approvedOn
            businessProfile {
                addresses {
                    address1
                    address2
                    addressType
                    city
                    country
                    postalCode
                    state
                }
                beneficialOwners {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                }
                businessEmail
                businessNature
                controlPersons {
                    citizenshipCountryCode
                    dateOfBirth
                    email
                    firstName
                    gender
                    id
                    lastName
                }
                dbaName
                employmentCategory
                establishedDate
                fundingAccount
                kybData {
                    description
                    documentExtension
                    documentId
                    documentName
                    documentSize
                    expiryDate
                    issuanceCountry
                    issuanceDate
                    type
                }
                kybEvaluations {
                    description
                    entityId
                    evaluationDateTime
                    evaluationStatus
                    flowCode
                    hostCode
                    hostEvaluationResponse
                    hostEvaluationStatus
                    id
                    lastStatusChangeComments
                    lastStatusChangedOn
                }
                kybResults {
                    description
                    docReviewStatus
                    entityId
                    flowId
                    isManuallyApproved
                    kycBad
                    kycGood
                    kycResultId
                    lastKycDatetime
                    pendingDocs
                    status
                }
                kybStatus
                legalName
                originCountry
                phoneDetails {
                    countryDialingCode
                    phoneNumber
                    type
                }
                salesRepresentative
                taxIdNumber
                websiteAddress
            }
            createdOn
            id
            partnerId
            status
            statusName
            type
        }
      }
    }`,
    {
      additionalData,
      affiliateId,
      businessProfile,
    },
  );
};
